/**
 * Fix for Diagnostic Cases creation
 * 
 * This script patches the global API instance to ensure proper handling of
 * diagnostic case creation and image uploads.
 */

// Import axios directly
import axios from 'axios';

export default function fixDiagnosticCaseApi(api) {
  console.log("Applying diagnostic cases fix");
  
  // Replace with fixed version
  api.createDiagnosticCase = function fixedCreateDiagnosticCase(caseData) {
    console.log('Enhanced createDiagnosticCase called with:', caseData);
    
    // Create FormData for proper file handling if not already FormData
    let formData;
    if (caseData instanceof FormData) {
      formData = caseData;
    } else {
      formData = new FormData();
      
      // Ensure exam_id is included and properly formatted
      let examId = caseData.exam_id;
      if (typeof examId === 'string') {
        examId = parseInt(examId, 10);
      }
      
      // Always add exam_id first
      formData.append('exam_id', examId.toString());
      console.log(`Added exam_id to FormData: ${examId}`);
      
      // Add all other fields from caseData
      Object.keys(caseData).forEach(key => {
        if (key === 'exam_id' || key === 'image' || key === 'imageFile' || key === 'imagePreview' || key === 'image_url') {
          return; // Skip these fields, we handle them specially
        }
        
        // Add field to FormData 
        if (caseData[key] !== null && caseData[key] !== undefined) {
          formData.append(key, caseData[key]);
        }
      });
      
      // Handle file upload
      if (caseData.imageFile instanceof File) {
        formData.append('image', caseData.imageFile);
        console.log(`Added image file: ${caseData.imageFile.name} (${caseData.imageFile.size} bytes)`);
      }
    }
    
    // Log what we're submitting
    console.log('Submitting diagnostic case with FormData:');
    for (const [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(`- ${key}: [File ${value.name}]`);
      } else {
        console.log(`- ${key}: ${value}`);
      }
    }
    
    // Create a direct axios-based API call to the backend
    // Get base URL - if api.getBaseUrl exists, use it, otherwise use a default
    const baseUrl = typeof api.getBaseUrl === 'function' ? api.getBaseUrl() : 'https://tutormed.org/api/';

    // Construct the full URL - ensure there's a trailing slash on the base URL
    const adjustedBaseUrl = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
    const url = `${adjustedBaseUrl}mocks/diagnostic/cases/`;
    
    // Clone the headers from the original API
    const headers = {
      'Content-Type': 'multipart/form-data'
    };
    
    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      headers['Authorization'] = `Token ${token}`;
    }
    
    console.log(`Making API request to: ${url}`);
    return axios.post(url, formData, { 
      headers,
      withCredentials: true
    });
  };
  
  // Also fix updateDiagnosticCase
  
  api.updateDiagnosticCase = function fixedUpdateDiagnosticCase(caseId, caseData) {
    console.log(`Enhanced updateDiagnosticCase called for case ${caseId}:`, caseData);
    
    // Create FormData for proper file handling if not already FormData
    let formData;
    if (caseData instanceof FormData) {
      formData = caseData;
    } else {
      formData = new FormData();
      
      // Ensure exam_id is included and properly formatted
      let examId = caseData.exam_id;
      if (typeof examId === 'string') {
        examId = parseInt(examId, 10);
      }
      
      // Always add exam_id first
      formData.append('exam_id', examId.toString());
      console.log(`Added exam_id to FormData: ${examId}`);
      
      // Add all other fields from caseData
      Object.keys(caseData).forEach(key => {
        if (key === 'exam_id' || key === 'image' || key === 'imageFile' || key === 'imagePreview' || key === 'image_url') {
          return; // Skip these fields, we handle them specially
        }
        
        // Add field to FormData 
        if (caseData[key] !== null && caseData[key] !== undefined) {
          formData.append(key, caseData[key]);
        }
      });
      
      // Handle file upload
      if (caseData.imageFile instanceof File) {
        formData.append('image', caseData.imageFile);
        console.log(`Added image file: ${caseData.imageFile.name} (${caseData.imageFile.size} bytes)`);
      }
    }
    
    // Log what we're submitting
    console.log('Submitting diagnostic case update with FormData:');
    for (const [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(`- ${key}: [File ${value.name}]`);
      } else {
        console.log(`- ${key}: ${value}`);
      }
    }
    
    // Create a direct axios-based API call to the backend
    // Get base URL with the same approach as create
    const baseUrl = typeof api.getBaseUrl === 'function' ? api.getBaseUrl() : 'https://tutormed.org/api/';

    // Construct the full URL - ensure there's a trailing slash on the base URL
    const adjustedBaseUrl = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
    const url = `${adjustedBaseUrl}mocks/diagnostic/cases/${caseId}/`;
    
    // Clone the headers from the original API
    const headers = {
      'Content-Type': 'multipart/form-data'
    };
    
    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      headers['Authorization'] = `Token ${token}`;
    }
    
    console.log(`Making API request to: ${url}`);
    // Use PUT for updates
    return axios.put(url, formData, { 
      headers,
      withCredentials: true
    });
  };
  
  console.log("Diagnostic cases fix applied successfully");
  return api;
}